import { useEffect, useRef } from "react";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import type { DateRange } from "@/components/AirbnbDatePicker";
import {
  ISO_DATE,
  errorResult,
  registerMcpTool,
  type McpToolDescriptor,
} from "@/hooks/use-webmcp";

// WebMCP (Web Model Context Protocol) lets an MCP-capable browser/agent discover
// and invoke page capabilities. Shared primitives (feature detection, tool
// registration with same-name dedup, `errorResult`, `ISO_DATE`) live in
// `use-webmcp.ts`; this file builds the booking-specific tools on top of them.
// Each tool DRIVES the existing UI + reuses existing endpoints — it never
// duplicates pricing logic. It stops at a quote + pre-fill; the human still pays.

// ── Cabin / property stay quote tool ──────────────────────────────────────────

// Direct cabin bookings allow up to 16 counted guests (the nightly rate
// includes 12; $100/guest/night applies beyond). Listing pages pass their own
// explicit maxGuests, so this default only governs the homepage cabin tool.
const DEFAULT_MAX_GUESTS = 16;

function buildBookingQuoteInputSchema(maxGuests: number) {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      checkIn: {
        type: "string",
        pattern: "^\\d{4}-\\d{2}-\\d{2}$",
        description: "Check-in date in ISO format (YYYY-MM-DD).",
      },
      checkOut: {
        type: "string",
        pattern: "^\\d{4}-\\d{2}-\\d{2}$",
        description: "Check-out date in ISO format (YYYY-MM-DD). Must be after checkIn.",
      },
      guests: {
        type: "integer",
        minimum: 1,
        maximum: maxGuests,
        description: `Number of guests staying (1-${maxGuests}).`,
      },
      discountCode: {
        type: "string",
        description: "Optional promo / discount code to check against this stay.",
      },
    },
    required: ["checkIn", "checkOut", "guests"],
  } as const;
}

export type BookingMcpHandlers = {
  /** Populate the booking widget's selected date range. */
  applyDates: (range: DateRange) => void;
  /** Populate the booking widget's guest count. */
  applyGuests: (guests: number) => void;
  /** Seed a discount code so it is pre-filled when the guest opens checkout. */
  applyDiscountCode?: (code: string) => void;
};

export type BookingMcpOptions = {
  /** Unique WebMCP tool name. Defaults to the cabin homepage tool. */
  toolName?: string;
  /** Human-facing description handed to the agent. */
  description?: string;
  /** Scope the quote (availability + pricing) to a specific property listing. */
  propertyId?: string;
  /** Property display name, used in the quote summary text. */
  propertyName?: string;
  /** Max guests the widget accepts (drives the input schema bound). */
  maxGuests?: number;
};

/**
 * Registers a single WebMCP tool that drives the existing booking widget. On
 * invocation it populates the widget's dates + guest count and returns a live
 * price quote from the existing `/api/pricing/quote` endpoint — it never
 * reimplements pricing. The human still confirms and pays.
 *
 * Used by both the homepage cabin widget (default options) and the listing-page
 * widget (scoped via `propertyId` + a distinct `toolName`).
 */
export function useBookingMcpTool(handlers: BookingMcpHandlers, options: BookingMcpOptions = {}) {
  // Keep the latest handlers/options in refs so the long-lived tool closure
  // always drives the current widget instance without re-registering on every
  // render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const toolName = options.toolName ?? "get_cabin_booking_quote";
  const maxGuests = options.maxGuests ?? DEFAULT_MAX_GUESTS;
  const description =
    options.description ??
    "Get a live price quote for a direct stay at the Legendary Log Cabin and pre-fill the on-page booking widget with the dates and guest count. Returns the nightly subtotal, the automatic direct-booking discount, the card processing fee, and the total before taxes. This does not charge anything — the guest still reviews and pays through Stripe.";

  useEffect(() => {
    const tool: McpToolDescriptor = {
      name: toolName,
      description,
      inputSchema: buildBookingQuoteInputSchema(maxGuests) as unknown as Record<string, unknown>,
      execute: async (args) => {
        const checkIn = typeof args.checkIn === "string" ? args.checkIn : "";
        const checkOut = typeof args.checkOut === "string" ? args.checkOut : "";
        const guestsRaw = args.guests;
        const discountCode =
          typeof args.discountCode === "string" ? args.discountCode.trim() : "";
        const propertyId = optionsRef.current.propertyId;
        const propertyName = optionsRef.current.propertyName;

        if (!ISO_DATE.test(checkIn) || !ISO_DATE.test(checkOut)) {
          return errorResult("checkIn and checkOut must be ISO dates (YYYY-MM-DD).");
        }
        const from = new Date(checkIn + "T12:00:00");
        const to = new Date(checkOut + "T12:00:00");
        if (isNaN(from.getTime()) || isNaN(to.getTime())) {
          return errorResult("checkIn / checkOut are not valid dates.");
        }
        if (to.getTime() <= from.getTime()) {
          return errorResult("checkOut must be after checkIn.");
        }
        const guests = Math.floor(Number(guestsRaw));
        if (!Number.isFinite(guests) || guests < 1 || guests > maxGuests) {
          return errorResult(`guests must be a whole number between 1 and ${maxGuests}.`);
        }

        // Drive the existing booking widget (dates + guests) so the human sees
        // the same selection the agent quoted.
        handlersRef.current.applyDates({ from, to });
        handlersRef.current.applyGuests(guests);
        if (discountCode) handlersRef.current.applyDiscountCode?.(discountCode);

        // Live quote from the SAME endpoint the widget uses — no pricing logic
        // is duplicated here. `propertyId` scopes availability + pricing to the
        // listing when present (homepage cabin flow omits it).
        let quote: {
          nights: number;
          subtotal: number;
          discountPercent: number;
          discountAmount: number;
          taxAmount: number;
          taxPct: number;
          processingFee: number;
          processingFeePct: number;
          extraGuestFee?: number;
          extraGuests?: number;
          total: number;
          avgNightly: number;
          dynamic: boolean;
        };
        try {
          const params = new URLSearchParams({ checkIn, checkOut });
          if (propertyId) params.set("propertyId", propertyId);
          if (guests) params.set("guests", String(guests));
          const res = await apiRequest("GET", `/api/pricing/quote?${params.toString()}`);
          quote = await res.json();
        } catch (err) {
          return errorResult(
            `Couldn't fetch a live quote: ${err instanceof Error ? err.message : "please try different dates."}`,
          );
        }

        // Optionally check the discount code against the same validation endpoint
        // the checkout uses. Purely informational — the server re-applies the
        // best-of discount at charge time.
        let discountCodeStatus: { code: string; valid: boolean; message?: string } | undefined;
        if (discountCode) {
          try {
            const res = await apiRequest("POST", "/api/discount-codes/validate", {
              code: discountCode,
            });
            const data = await res.json();
            discountCodeStatus = { code: discountCode, valid: res.ok, message: data?.message };
          } catch {
            discountCodeStatus = { code: discountCode, valid: false, message: "Could not validate code." };
          }
        }

        const stayLabel = propertyName ?? "the Legendary Log Cabin";

        const structured = {
          ...(propertyId ? { propertyId } : {}),
          ...(propertyName ? { propertyName } : {}),
          checkIn,
          checkOut,
          guests,
          nights: quote.nights,
          currency: "USD",
          avgNightly: quote.avgNightly,
          subtotal: quote.subtotal,
          directBookingDiscount: {
            percent: quote.discountPercent,
            amount: quote.discountAmount,
          },
          cardProcessingFee: {
            percent: quote.processingFeePct,
            amount: quote.processingFee,
          },
          totalBeforeTaxes: quote.total,
          ...((quote.extraGuestFee ?? 0) > 0 ? { extraGuestFee: { amount: quote.extraGuestFee ?? 0, guestsOverIncluded: quote.extraGuests ?? 0 } } : {}),
          dynamicPricing: quote.dynamic,
          ...(discountCodeStatus ? { discountCode: discountCodeStatus } : {}),
          note: "Widget pre-filled. No charge has been made; the guest completes payment through Stripe.",
        };

        const summary = [
          `Quote for ${stayLabel} — ${quote.nights} night${quote.nights !== 1 ? "s" : ""}, ${guests} guest${guests !== 1 ? "s" : ""} (${format(from, "MMM d")}–${format(to, "MMM d, yyyy")}):`,
          `• Subtotal: $${quote.subtotal.toLocaleString()} ($${quote.avgNightly.toLocaleString()}/night avg)`,
          quote.discountAmount > 0
            ? `• Direct-booking discount: -$${quote.discountAmount.toLocaleString()}`
            : null,
          quote.taxAmount > 0
            ? `• Taxes (MN 6.875% + Douglas Co. 0.5%): $${quote.taxAmount.toLocaleString()}`
            : null,
          `• Card processing fee (${quote.processingFeePct}%): $${quote.processingFee.toLocaleString()}`,
          `• Total: $${quote.total.toLocaleString()}`,
          discountCodeStatus
            ? `• Code "${discountCodeStatus.code}": ${discountCodeStatus.valid ? "valid" : `not applicable${discountCodeStatus.message ? ` (${discountCodeStatus.message})` : ""}`}`
            : null,
          `The booking widget is now pre-filled. No charge has been made — the guest confirms and pays through Stripe.`,
        ]
          .filter(Boolean)
          .join("\n");

        return {
          content: [{ type: "text", text: summary }],
          structuredContent: structured,
        };
      },
    };

    return registerMcpTool(tool);
    // Re-register only if the tool identity changes.
  }, [toolName, description, maxGuests]);
}

// ── Pontoon rental quote tool ─────────────────────────────────────────────────

/** Coupon shape shared with the pontoon booking form. */
export type PontoonCouponDiscount = {
  type: "waive" | "amount" | "flat_total" | "percent";
  amount: number;
};

/** Result of the pontoon pricing math (mirrors the form's own computation). */
export type PontoonQuoteResult = {
  rawBase: number;
  ratePerDay: number;
  discount: number;
  base: number;
  fullyWaived: boolean;
  surcharge: number;
  total: number;
};

export type PontoonMcpConfig = {
  /** Duration bounds from the live pontoon pricing endpoint. */
  minDays: number;
  maxDays: number;
  /**
   * The pontoon form's OWN pricing function (ladder + coupon + card surcharge),
   * so the tool's quote is identical to what the widget shows — no reimplementation.
   */
  computeQuote: (
    nights: number,
    couponDiscount: PontoonCouponDiscount | null,
    paymentMethod: "card" | "cash",
  ) => PontoonQuoteResult;
  /** Populate the pontoon widget's rental dates (YYYY-MM-DD). */
  applyDates: (checkIn: string, checkOut: string) => void;
  /** Apply a validated coupon to the widget so its total reflects the quote. */
  applyCoupon?: (code: string, discount: PontoonCouponDiscount) => void;
};

const pontoonQuoteInputSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    checkIn: {
      type: "string",
      pattern: "^\\d{4}-\\d{2}-\\d{2}$",
      description: "First rental day in ISO format (YYYY-MM-DD).",
    },
    checkOut: {
      type: "string",
      pattern: "^\\d{4}-\\d{2}-\\d{2}$",
      description: "Return day in ISO format (YYYY-MM-DD). Must be after checkIn.",
    },
    couponCode: {
      type: "string",
      description: "Optional coupon code to check against this pontoon rental.",
    },
  },
  required: ["checkIn", "checkOut"],
} as const;

function mapPontoonCouponType(
  discountType: unknown,
): PontoonCouponDiscount["type"] {
  return discountType === "amount"
    ? "amount"
    : discountType === "flat_total"
      ? "flat_total"
      : discountType === "percent"
        ? "percent"
        : "waive";
}

/**
 * Registers a single WebMCP tool ("get_pontoon_booking_quote") that drives the
 * pontoon booking form. It computes the quote with the form's OWN pricing
 * function (ladder-based) and validates coupons against the existing
 * `/api/pontoon/validate-coupon` endpoint — no pricing logic is duplicated. It
 * pre-fills the dates (and coupon) and stops at a quote; the human still books
 * and pays.
 */
export function usePontoonMcpTool(config: PontoonMcpConfig) {
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    const tool: McpToolDescriptor = {
      name: "get_pontoon_booking_quote",
      description:
        "Get a live price quote for renting the pontoon boat at the Legendary Log Cabin and pre-fill the on-page pontoon booking form with the rental dates. Returns the base rental cost (ladder pricing by number of days), any coupon discount, the card processing fee, and the total. This does not charge anything — the guest still completes the waiver and pays.",
      inputSchema: pontoonQuoteInputSchema as unknown as Record<string, unknown>,
      execute: async (args) => {
        const cfg = configRef.current;
        const checkIn = typeof args.checkIn === "string" ? args.checkIn : "";
        const checkOut = typeof args.checkOut === "string" ? args.checkOut : "";
        const couponCode = typeof args.couponCode === "string" ? args.couponCode.trim() : "";

        if (!ISO_DATE.test(checkIn) || !ISO_DATE.test(checkOut)) {
          return errorResult("checkIn and checkOut must be ISO dates (YYYY-MM-DD).");
        }
        const from = new Date(checkIn + "T12:00:00");
        const to = new Date(checkOut + "T12:00:00");
        if (isNaN(from.getTime()) || isNaN(to.getTime())) {
          return errorResult("checkIn / checkOut are not valid dates.");
        }
        const nights = Math.round((to.getTime() - from.getTime()) / 86400000);
        if (!Number.isFinite(nights) || nights <= 0) {
          return errorResult("checkOut must be after checkIn.");
        }
        if (nights < cfg.minDays) {
          return errorResult(`Minimum pontoon rental is ${cfg.minDays} day${cfg.minDays === 1 ? "" : "s"}.`);
        }
        if (nights > cfg.maxDays) {
          return errorResult(`Maximum pontoon rental is ${cfg.maxDays} day${cfg.maxDays === 1 ? "" : "s"}.`);
        }

        // Validate the coupon against the SAME endpoint the form uses. The server
        // is the source of truth for discount type + amount.
        let couponDiscount: PontoonCouponDiscount | null = null;
        let couponStatus: { code: string; valid: boolean; message?: string } | undefined;
        if (couponCode) {
          try {
            const res = await apiRequest("POST", "/api/pontoon/validate-coupon", {
              code: couponCode,
            });
            const data = await res.json();
            if (data?.valid) {
              couponDiscount = {
                type: mapPontoonCouponType(data.discountType),
                amount: Number(data.discountAmount) || 0,
              };
              couponStatus = { code: couponCode, valid: true };
            } else {
              couponStatus = { code: couponCode, valid: false, message: "Invalid code — check with your host." };
            }
          } catch {
            couponStatus = { code: couponCode, valid: false, message: "Could not validate code." };
          }
        }

        // Quote via the form's OWN pricing function so the numbers match the UI.
        // Default to card (the form's default) so the quoted total matches what
        // most guests see.
        const q = cfg.computeQuote(nights, couponDiscount, "card");

        // Drive the pontoon form: set dates and, if the coupon is valid, apply it.
        cfg.applyDates(checkIn, checkOut);
        if (couponDiscount) cfg.applyCoupon?.(couponCode, couponDiscount);

        const structured = {
          checkIn,
          checkOut,
          days: nights,
          currency: "USD",
          ratePerDay: q.ratePerDay,
          baseRentalCost: q.rawBase,
          ...(couponDiscount ? { couponDiscount: q.discount } : {}),
          subtotalAfterDiscount: q.base,
          cardProcessingFee: q.surcharge,
          cashTotal: q.base,
          cardTotal: q.total,
          ...(couponStatus ? { couponCode: couponStatus } : {}),
          note: "Pontoon form pre-filled. No charge has been made; the guest completes the waiver and pays.",
        };

        const summary = [
          `Pontoon quote for ${nights} day${nights !== 1 ? "s" : ""} (${format(from, "MMM d")}–${format(to, "MMM d, yyyy")}):`,
          `• Base rental: $${q.rawBase.toLocaleString()}`,
          couponDiscount && q.discount > 0
            ? `• Coupon "${couponCode}": -$${q.discount.toLocaleString()}`
            : null,
          q.surcharge > 0
            ? `• Card processing fee: $${q.surcharge.toLocaleString()} (waived if paying cash)`
            : null,
          `• Total: $${q.total.toLocaleString()}${q.surcharge > 0 ? ` by card, $${q.base.toLocaleString()} by cash` : ""}`,
          couponStatus && !couponStatus.valid
            ? `• Code "${couponStatus.code}": not applicable${couponStatus.message ? ` (${couponStatus.message})` : ""}`
            : null,
          `The pontoon form is now pre-filled. No charge has been made — the guest completes the waiver and pays.`,
        ]
          .filter(Boolean)
          .join("\n");

        return {
          content: [{ type: "text", text: summary }],
          structuredContent: structured,
        };
      },
    };

    return registerMcpTool(tool);
  }, []);
}
